import React, { useRef, forwardRef, useEffect, useImperativeHandle } from 'react';

export interface ErpFormFileProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value?: File | File[] | null; // Replaces modelValue
  accept?: string;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  state?: boolean | null;
  browseText?: string;
  onChange?: (value: File | File[] | null, event: React.ChangeEvent<HTMLInputElement>) => void;
}

import { Input } from "@/components/ui/input";

export const ErpFormFile = forwardRef<HTMLInputElement, ErpFormFileProps>(({
  value: modelValue,
  accept,
  multiple = false,
  placeholder: _placeholder, // Kept for API compatibility
  disabled = false,
  state = null,
  browseText: _browseText = 'Browse', // Kept for API compatibility
  onChange,
  className,
  ...attrs
}, ref) => {
  const internalRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => internalRef.current!);

  // Reflect the controlled value into the file input's displayed selection:
  // a null/undefined value clears it, File instance(s) are re-assigned via
  // DataTransfer (skipped when the input already holds the same selection).
  useEffect(() => {
    const input = internalRef.current;
    if (!input || typeof DataTransfer === 'undefined') return;
    const files = (Array.isArray(modelValue) ? modelValue : [modelValue])
      .filter((f): f is File => f instanceof File);
    if (files.length > 0) {
      const current = Array.from(input.files ?? []);
      if (current.length === files.length && current.every((f, i) => f === files[i])) return;
      const dt = new DataTransfer();
      files.forEach((file) => dt.items.add(file));
      const writable = input as HTMLInputElement & { files: FileList | null };
      writable.files = dt.files;
    } else if (modelValue === null && input.files && input.files.length > 0) {
      input.value = '';
    }
  }, [modelValue]);

  const stateClass = state === false ? 'border-red-500 focus-visible:ring-red-500' : state === true ? 'border-green-500 focus-visible:ring-green-500' : '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const nextValue = multiple ? files : (files[0] ?? null);
    onChange?.(nextValue, e);
  };

  return (
    <Input
      ref={internalRef}
      {...attrs}
      type="file"
      className={[stateClass, className].filter(Boolean).join(' ')}
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      onChange={handleChange}
    />
  );
});
